<template>
  <div class="study-planning">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          学习规划
          <span v-if="plans.length" class="plan-count">共 {{ plans.length }} 个计划</span>
        </h1>
        <div class="header-actions">
          <el-button type="primary" @click="createNewPlan">
            <i class="fa fa-plus"></i> 创建新计划
          </el-button>
        </div>
      </div>
    </div>

    <!-- 计划列表 -->
    <div v-if="planLoading" v-loading="true" class="plan-loading"></div>
    <template v-else-if="plans.length">
      <div v-for="plan in plans" :key="plan.id" class="current-plan">
        <div class="plan-header">
          <div class="plan-info">
            <h2 class="plan-title">
              {{ plan.title }}
              <span class="plan-subject">{{ plan.subject }}</span>
            </h2>
            <div class="plan-meta">
              <span class="plan-period">{{ formatDate(plan.startDate) }} - {{ formatDate(plan.endDate) }}</span>
              <span class="plan-status" :class="plan.status">{{ getPlanStatusText(plan.status) }}</span>
              <span class="plan-daily">每日 {{ formatStudyTime(plan.dailyStudyTime) }}</span>
            </div>
            <div v-if="plan.description" class="plan-desc">{{ plan.description }}</div>
          </div>
          <div class="plan-progress">
            <div class="progress-circle">
              <svg width="120" height="120">
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#e6e6e6"
                  stroke-width="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#3498db"
                  stroke-width="8"
                  stroke-linecap="round"
                  :stroke-dasharray="circumference"
                  :stroke-dashoffset="circumference - (plan.progress / 100) * circumference"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div class="progress-text">
                <span class="progress-value">{{ plan.progress }}%</span>
                <span class="progress-label">完成度</span>
              </div>
            </div>
          </div>
          <el-button
            class="plan-delete"
            type="text"
            icon="el-icon-delete"
            @click="removePlan(plan)"
          >删除</el-button>
        </div>

        <!-- 目标对比 -->
        <div class="goal-comparison">
          <span>当前成绩 {{ plan.currentScore }}分</span>
          <span class="goal-sep">→</span>
          <span>目标成绩 {{ plan.targetScore }}分</span>
          <span>预计提升 +{{ improvementOf(plan) }}分</span>
        </div>

        <!-- 里程碑进度 -->
        <div v-if="plan.milestones && plan.milestones.length" class="milestones">
          <h3>阶段目标</h3>
          <div class="milestone-list">
            <div 
              v-for="(milestone, index) in plan.milestones" 
              :key="index"
              class="milestone-item"
              :class="{ 
                completed: milestone.completed,
                current: milestone.current,
                upcoming: !milestone.completed && !milestone.current
              }"
            >
              <div class="milestone-icon">
                <i class="fa" :class="getMilestoneIcon(milestone)"></i>
              </div>
              <div class="milestone-content">
                <div class="milestone-title">{{ milestone.title }}</div>
                <div class="milestone-desc">{{ milestone.description }}</div>
                <div class="milestone-date">{{ formatDate(milestone.targetDate) }}</div>
              </div>
              <div class="milestone-progress">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: milestone.progress + '%' }"></div>
                </div>
                <span class="progress-text">{{ milestone.progress }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 无计划状态 -->
    <div v-else class="no-plan">
      <div class="no-plan-content">
        <div class="no-plan-icon">
          <i class="fa fa-calendar-plus"></i>
        </div>
        <h3>还没有学习计划</h3>
        <p>创建一个学习计划，让我们帮你制定科学的学习路径</p>
        <el-button type="primary" size="large" @click="createNewPlan">
          创建学习计划
        </el-button>
      </div>
    </div>

    <!-- 学习统计 -->
    <div class="study-stats">
      <div class="stats-header">
        <h3>学习统计</h3>
        <div class="time-filter">
          <el-radio-group v-model="statsTimeRange" @change="loadStudyStats">
            <el-radio-button label="week">本周</el-radio-button>
            <el-radio-button label="month">本月</el-radio-button>
            <el-radio-button label="quarter">本学期</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      
      <div v-loading="statsLoading" class="stats-content">
        <!-- 学习时长统计 -->
        <div class="stat-card">
          <div class="stat-header">
            <h4>学习时长趋势</h4>
          </div>
          <div class="chart-container">
            <div ref="studyTimeChart" class="chart"></div>
          </div>
        </div>

        <!-- 成绩变化 -->
        <div class="stat-card">
          <div class="stat-header">
            <h4>成绩变化</h4>
          </div>
          <div class="chart-container">
            <div ref="scoreChart" class="chart"></div>
          </div>
        </div>

        <!-- 知识点掌握情况 -->
        <div class="stat-card">
          <div class="stat-header">
            <h4>知识点掌握情况</h4>
          </div>
          <div class="knowledge-stats">
            <div 
              v-for="knowledge in knowledgeStats" 
              :key="knowledge.id"
              class="knowledge-item"
            >
              <div class="knowledge-name">{{ knowledge.name }}</div>
              <div class="knowledge-progress">
                <div class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ 
                      width: knowledge.mastery + '%',
                      backgroundColor: getKnowledgeColor(knowledge.mastery)
                    }"
                  ></div>
                </div>
                <span class="progress-value">{{ knowledge.mastery }}%</span>
              </div>
            </div>
            <div v-if="!statsLoading && !knowledgeStats.length" class="knowledge-empty">
              暂无知识点数据，完善学生档案的薄弱知识点后即可展示
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 创建计划对话框 -->
    <el-dialog
      title="创建学习计划"
      :visible.sync="createPlanVisible"
      width="600px"
      center
    >
      <el-form :model="newPlan" :rules="planRules" ref="planForm" label-width="100px">
        <el-form-item label="计划名称" prop="title">
          <el-input v-model="newPlan.title" placeholder="请输入计划名称"></el-input>
        </el-form-item>

        <el-form-item label="学科" prop="subject">
          <div v-loading="subjectsLoading" class="subject-options">
            <div
              v-for="subject in subjects"
              :key="subject.dictId || subject.id"
              class="subject-option"
              :class="{ active: newPlan.subject === subject.subject }"
              @click="newPlan.subject = subject.subject"
            >
              {{ subject.nickname || subject.subject }}
            </div>
            <div v-if="!subjectsLoading && !subjects.length" class="subject-empty">暂无学科可选</div>
          </div>
        </el-form-item>
        
        <el-form-item label="提升分数" prop="scoreImprovement">
          <div class="slider-with-value">
            <span class="slider-value">{{ newPlan.scoreImprovement }}分</span>
            <el-slider
              v-model="newPlan.scoreImprovement"
              :min="1"
              :max="20"
              :step="1"
              show-stops
            ></el-slider>
          </div>
        </el-form-item>
        
        <el-form-item label="每日学习时长">
          <div class="slider-with-value">
            <span class="slider-value">{{ formatStudyTime(newPlan.dailyStudyTime) }}</span>
            <el-slider
              v-model="newPlan.dailyStudyTime"
              :min="10"
              :max="120"
              :step="10"
              show-stops
              :format-tooltip="formatStudyTime"
            ></el-slider>
          </div>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="createPlanVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPlan" :loading="submitting">创建计划</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { axios } from "../../config/axios";
import moment from 'moment';
import * as echarts from 'echarts';

export default {
  name: 'StudyPlanning',
  data() {
    return {
      // 学习计划列表（一个学生可以有多个并行计划）
      plans: [],
      planLoading: true,
      circumference: 2 * Math.PI * 50, // 圆周长
      
      // 统计数据
      statsTimeRange: 'week',
      statsLoading: false,
      statsData: null,
      knowledgeStats: [],
      
      // 创建计划
      createPlanVisible: false,
      submitting: false,
      subjectsLoading: false,
      subjects: [],
      newPlan: {
        title: '',
        subject: '',
        scoreImprovement: 5,
        dailyStudyTime: 30
      },
      planRules: {
        title: [
          { required: true, message: '请输入计划名称', trigger: 'blur' }
        ],
        subject: [
          { required: true, message: '请选择学科', trigger: 'change' }
        ]
      },
      
      // 图表实例
      studyTimeChart: null,
      scoreChart: null
    }
  },
  created() {
    this.loadPlans();
    this.loadStudyStats();
    this.loadSubjects();
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
      // 统计接口可能先于图表容器渲染完成，这里补一次绘制
      this.updateCharts(this.statsData);
    });
    window.addEventListener('resize', this.resizeCharts);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts);
    if (this.studyTimeChart) {
      this.studyTimeChart.dispose();
    }
    if (this.scoreChart) {
      this.scoreChart.dispose();
    }
  },
  methods: {
    async loadPlans() {
      this.planLoading = true;
      try {
        const response = await axios.get('/student/plans');
        const res = response.data || {};
        if (res.code && res.code !== 200) {
          this.$message.error(res.message || '加载学习计划失败');
          return;
        }
        this.plans = res.data || [];
      } catch (error) {
        console.error('加载学习计划失败:', error);
        this.$message.error('加载学习计划失败');
      } finally {
        this.planLoading = false;
      }
    },
    
    improvementOf(plan) {
      const diff = Number(plan.targetScore) - Number(plan.currentScore);
      return Number.isFinite(diff) ? diff : 0;
    },
    
    async removePlan(plan) {
      try {
        await this.$confirm(`确定删除「${plan.title}」吗？`, '删除计划', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        });
      } catch (e) {
        return;
      }
      
      try {
        const response = await axios.post('/student/delete-plan', { planId: plan.id });
        const res = response.data || {};
        if (res.code && res.code !== 200) {
          this.$message.error(res.message || '删除计划失败');
          return;
        }
        this.$message.success('计划已删除');
        this.plans = this.plans.filter(item => item.id !== plan.id);
        this.loadStudyStats();
      } catch (error) {
        console.error('删除计划失败:', error);
        this.$message.error('删除计划失败');
      }
    },
    
    async loadStudyStats() {
      this.statsLoading = true;
      try {
        const response = await axios.get('/student/study-stats', {
          params: { timeRange: this.statsTimeRange }
        });
        const res = response.data || {};
        if (res.code && res.code !== 200) {
          this.$message.error(res.message || '加载学习统计失败');
          return;
        }
        const data = res.data || {};
        this.statsData = data;
        this.knowledgeStats = data.knowledgeStats || [];
        
        // 更新图表数据
        this.updateCharts(data);
        
      } catch (error) {
        console.error('加载学习统计失败:', error);
        this.$message.error('加载学习统计失败');
      } finally {
        this.statsLoading = false;
      }
    },
    
    initCharts() {
      // 初始化学习时长图表
      if (this.$refs.studyTimeChart && !this.studyTimeChart) {
        this.studyTimeChart = echarts.init(this.$refs.studyTimeChart);
      }
      
      // 初始化成绩图表
      if (this.$refs.scoreChart && !this.scoreChart) {
        this.scoreChart = echarts.init(this.$refs.scoreChart);
      }
    },
    
    resizeCharts() {
      if (this.studyTimeChart) {
        this.studyTimeChart.resize();
      }
      if (this.scoreChart) {
        this.scoreChart.resize();
      }
    },
    
    updateCharts(data) {
      if (!data) {
        return;
      }
      this.initCharts();

      // 更新学习时长图表
      if (this.studyTimeChart && data.studyTimeData) {
        const option = {
          tooltip: {
            trigger: 'axis'
          },
          xAxis: {
            type: 'category',
            data: data.studyTimeData.dates
          },
          yAxis: {
            type: 'value',
            name: '学习时长(分钟)'
          },
          series: [{
            data: data.studyTimeData.values,
            type: 'line',
            smooth: true,
            itemStyle: {
              color: '#3498db'
            }
          }]
        };
        this.studyTimeChart.setOption(option);
      }
      
      // 更新成绩图表
      if (this.scoreChart && data.scoreData) {
        const option = {
          tooltip: {
            trigger: 'axis'
          },
          xAxis: {
            type: 'category',
            data: data.scoreData.dates
          },
          yAxis: {
            type: 'value',
            name: '成绩',
            min: 0,
            max: 100
          },
          series: [{
            data: data.scoreData.values,
            type: 'line',
            smooth: true,
            itemStyle: {
              color: '#27ae60'
            }
          }]
        };
        this.scoreChart.setOption(option);
      }
    },
    
    async loadSubjects() {
      this.subjectsLoading = true;
      try {
        const response = await axios.get('/student/gradeSubject');
        this.subjects = response.data.data || [];
      } catch (error) {
        console.error('加载学科列表失败:', error);
        this.subjects = [];
      } finally {
        this.subjectsLoading = false;
      }
    },

    createNewPlan() {
      this.createPlanVisible = true;
      this.resetPlanForm();
      if (!this.subjects.length) {
        this.loadSubjects();
      }
    },
    
    resetPlanForm() {
      this.newPlan = {
        title: '',
        subject: '',
        scoreImprovement: 5,
        dailyStudyTime: 30
      };
      if (this.$refs.planForm) {
        this.$refs.planForm.resetFields();
      }
    },
    
    async submitPlan() {
      const valid = await this.$refs.planForm.validate().catch(() => false);
      if (!valid) {
        return;
      }
      
      this.submitting = true;
      try {
        const response = await axios.post('/student/create-plan', { ...this.newPlan }, {
          headers: { 'Content-Type': 'application/json' },
          transformRequest: [(data) => JSON.stringify(data)]
        });
        const res = response.data || {};
        if (res.code && res.code !== 200) {
          this.$message.error(res.message || '创建计划失败');
          return;
        }
        
        this.$message.success('学习计划创建成功');
        this.createPlanVisible = false;
        if (res.data) {
          this.plans.unshift(res.data);
        } else {
          this.loadPlans();
        }
        // 计划变化会影响成绩趋势基线，同步刷新统计
        this.loadStudyStats();
        
      } catch (error) {
        console.error('创建计划失败:', error);
        this.$message.error('创建计划失败');
      } finally {
        this.submitting = false;
      }
    },
    
    getMilestoneIcon(milestone) {
      if (milestone.completed) return 'fa-check-circle';
      if (milestone.current) return 'fa-play-circle';
      return 'fa-circle-o';
    },
    
    getPlanStatusText(status) {
      const statusMap = {
        active: '进行中',
        completed: '已完成',
        paused: '已暂停',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    },
    
    getKnowledgeColor(mastery) {
      if (mastery >= 80) return '#27ae60';
      if (mastery >= 60) return '#f39c12';
      return '#e74c3c';
    },
    
    formatDate(date) {
      if (!date) {
        return '--';
      }
      const parsed = moment(date);
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '--';
    },
    
    formatStudyTime(value) {
      const hours = Math.floor(value / 60);
      const minutes = value % 60;
      if (hours > 0) {
        return `${hours}小时${minutes > 0 ? minutes + '分钟' : ''}`;
      }
      return `${minutes}分钟`;
    }
  }
}
</script>

<style lang="less" scoped>
.study-planning {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100%;
}

// 页面头部
.page-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.plan-count {
  margin-left: 12px;
  font-size: 14px;
  font-weight: 400;
  color: #909399;
}

.slider-with-value {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.slider-value {
  flex-shrink: 0;
  min-width: 72px;
  font-size: 14px;
  font-weight: 600;
  color: #3498db;
  text-align: right;
}

.slider-with-value .el-slider {
  flex: 1;
}

.subject-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  min-height: 36px;
}

.subject-option {
  padding: 8px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fff;
  color: #606266;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    border-color: #3498db;
    color: #3498db;
  }

  &.active {
    border-color: #3498db;
    background: #e3f2fd;
    color: #1976d2;
    font-weight: 500;
  }
}

.subject-empty {
  color: #909399;
  font-size: 13px;
}

.plan-loading {
  background: white;
  border-radius: 12px;
  height: 220px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

// 当前计划
.current-plan {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.plan-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.plan-info {
  flex: 1;
}

.plan-title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

.plan-subject {
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 10px;
  background-color: #eef4fb;
  color: #3498db;
  font-size: 12px;
  font-weight: 500;
  vertical-align: middle;
}

.plan-desc {
  margin-top: 10px;
  max-width: 560px;
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.6;
}

.plan-delete {
  position: absolute;
  top: -8px;
  right: 0;
  color: #c0c4cc;
  padding: 0;

  &:hover {
    color: #e74c3c;
  }
}

.plan-meta {
  display: flex;
  gap: 16px;
  align-items: center;
}

.plan-period,
.plan-daily {
  color: #7f8c8d;
  font-size: 14px;
}

.plan-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.active {
    background-color: #e3f2fd;
    color: #1976d2;
  }
  
  &.completed {
    background-color: #e8f5e9;
    color: #2e7d32;
  }
  
  &.paused {
    background-color: #fff3e0;
    color: #e65100;
  }
  
  &.cancelled {
    background-color: #ffebee;
    color: #c62828;
  }
}

// 进度圆环
.progress-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progress-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #3498db;
}

.progress-label {
  font-size: 12px;
  color: #7f8c8d;
}

// 目标对比
.goal-comparison {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin-bottom: 24px;
  color: #7f8c8d;
  font-size: 14px;
  line-height: 1.6;
}

.goal-sep {
  color: #bdc3c7;
}

// 里程碑
.milestones {
  h3 {
    margin-bottom: 20px;
    color: #2c3e50;
  }
}

.milestone-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.milestone-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  transition: all 0.2s;
  
  &.completed {
    background-color: #e8f5e9;
    border-left: 4px solid #27ae60;
    
    .milestone-icon i {
      color: #27ae60;
    }
  }
  
  &.current {
    background-color: #e3f2fd;
    border-left: 4px solid #3498db;
    
    .milestone-icon i {
      color: #3498db;
    }
  }
  
  &.upcoming {
    background-color: #f8f9fa;
    border-left: 4px solid #e0e0e0;
    
    .milestone-icon i {
      color: #95a5a6;
    }
  }
}

.milestone-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  
  i {
    font-size: 20px;
  }
}

.milestone-content {
  flex: 1;
  margin-right: 16px;
}

.milestone-title {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.milestone-desc {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 4px;
}

.milestone-date {
  font-size: 12px;
  color: #95a5a6;
}

.milestone-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 120px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #3498db;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #7f8c8d;
  min-width: 35px;
}

// 无计划状态
.no-plan {
  background: white;
  border-radius: 12px;
  padding: 60px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.no-plan-icon {
  font-size: 64px;
  color: #bdc3c7;
  margin-bottom: 24px;
}

.no-plan-content {
  h3 {
    margin-bottom: 12px;
    color: #2c3e50;
  }
  
  p {
    color: #7f8c8d;
    margin-bottom: 24px;
  }
}

// 学习统计
.study-stats {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h3 {
    margin: 0;
    color: #2c3e50;
  }
}

.stats-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
}

.stat-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
}

.stat-header {
  margin-bottom: 16px;
  
  h4 {
    margin: 0;
    color: #2c3e50;
  }
}

.chart-container {
  height: 200px;
}

.chart {
  width: 100%;
  height: 100%;
}

// 知识点统计
.knowledge-stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.knowledge-item {
  display: flex;
  align-items: center;
  gap: 16px;
}

.knowledge-empty {
  color: #909399;
  font-size: 13px;
  text-align: center;
  padding: 24px 0;
}

.knowledge-name {
  min-width: 100px;
  font-weight: 500;
  color: #2c3e50;
}

.knowledge-progress {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-value {
  font-size: 14px;
  color: #7f8c8d;
  min-width: 40px;
}

// 响应式设计
@media (max-width: 1200px) {
  .stats-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .study-planning {
    padding: 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 16px;
  }
  
  .plan-header {
    flex-direction: column;
    gap: 24px;
    text-align: center;
  }
  
  .milestone-item {
    flex-direction: column;
    text-align: center;
  }
  
  .milestone-progress {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .stats-content {
    grid-template-columns: 1fr;
  }
  
  .chart-container {
    height: 150px;
  }
  
  .knowledge-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .knowledge-progress {
    width: 100%;
  }
}
</style>
